import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";
import { api, STATUS_ORDER, STATUS_LABELS } from "../lib/api";

/* ─── Intake schema ─── */
// Step 1 — About you & the business
const STEP_1_FIELDS = [
  ["company_name", { en: "Company name", es: "Nombre de la compañía" }, "input"],
  ["country_city", { en: "Country / City", es: "País / Ciudad" }, "input"],
  ["industry", { en: "Industry / category", es: "Industria / categoría" }, "input"],
  ["website", { en: "Website / Instagram / current materials", es: "Web / Instagram / materiales actuales" }, "input"],
  ["business_in_one_sentence", { en: "Describe your business in one sentence", es: "Describe tu negocio en una frase" }, "textarea"],
  ["selling_what", { en: "What are you selling?", es: "¿Qué estás vendiendo?" }, "textarea"],
  ["audience", { en: "Who is your audience?", es: "¿Quién es tu audiencia?" }, "textarea"],
  ["branding_budget", { en: "Branding / marketing budget (approx, optional)", es: "Presupuesto de branding / marketing (aprox, opcional)" }, "input"],
];

// Step 2 — Positioning, competition & goals (universal + service-specific)
const STEP_2_UNIVERSAL = [
  ["why_should_someone_choose_you", { en: "Why should someone choose you over a competitor?", es: "¿Por qué alguien debería elegirte a ti y no a un competidor?" }, "textarea"],
  ["competitors_admire", { en: "Competitors you admire (with links)", es: "Competidores que admiras (con links)" }, "textarea"],
  ["brands_you_love", { en: "Brands you love (any industry)", es: "Marcas que amas (cualquier industria)" }, "textarea"],
  ["business_goal_12_months", { en: "Main business goal in the next 12 months", es: "Principal objetivo del negocio en los próximos 12 meses" }, "textarea"],
  ["biggest_obstacle", { en: "Biggest obstacle right now", es: "Mayor obstáculo ahora mismo" }, "textarea"],
  ["anything_else", { en: "Anything else we should know", es: "Cualquier otra cosa que debamos saber" }, "textarea"],
];

const STEP_2_SPECIFIC = {
  "brand-scan": [
    ["whats_not_working", { en: "What is not working for you in this brand?", es: "¿Qué no está funcionando en esta marca?" }, "textarea"],
    ["audience_clarity", { en: "Do you feel your audience understands what you do? Why or why not?", es: "¿Sientes que tu audiencia entiende lo que haces? ¿Por qué?" }, "textarea"],
    ["recent_results", { en: "Recent results (last 90 days): sales, leads, growth", es: "Resultados recientes (últimos 90 días): ventas, leads, crecimiento" }, "textarea"],
    ["assets_links", { en: "Links to website, IG, decks, recent content", es: "Links a web, IG, decks, contenido reciente" }, "textarea"],
  ],
  "brand-development": [
    ["why_starting", { en: "Why are you starting this brand now?", es: "¿Por qué arrancas esta marca ahora?" }, "textarea"],
    ["category_position", { en: "Where in the category do you want to position?", es: "¿En qué lugar de la categoría te quieres posicionar?" }, "textarea"],
    ["price_point", { en: "Price point and offer logic", es: "Precio y lógica de la oferta" }, "textarea"],
    ["aesthetic_direction", { en: "Aesthetic direction you're drawn to (references / moodboard)", es: "Dirección estética que te atrae (referencias / moodboard)" }, "textarea"],
    ["launch_month", { en: "Target launch month", es: "Mes objetivo de lanzamiento" }, "input"],
  ],
  "brand-strategy": [
    ["current_story", { en: "Current story / how you currently explain the brand", es: "Historia actual / cómo explicas la marca hoy" }, "textarea"],
    ["what_brand_means", { en: "What should this brand mean beyond what it sells?", es: "¿Qué debería significar esta marca más allá de lo que vende?" }, "textarea"],
    ["cultural_references", { en: "Cultural references: films, music, places, authors, eras", es: "Referencias culturales: films, música, lugares, autores, épocas" }, "textarea"],
    ["tone_of_voice", { en: "Tone of voice you want (and what to avoid)", es: "Tono de voz deseado (y a evitar)" }, "textarea"],
    ["narrative_problem", { en: "What's missing in how the brand currently communicates?", es: "¿Qué falta en cómo comunica hoy la marca?" }, "textarea"],
  ],
  "brand-partner": [
    ["business_context", { en: "Quick business context for the month ahead", es: "Contexto del negocio para el próximo mes" }, "textarea"],
    ["first_priority", { en: "First priority you want me to look at", es: "Primera prioridad que quieres que revise" }, "textarea"],
    ["working_with", { en: "Who else is working on the brand right now", es: "Quién más está trabajando en la marca ahora" }, "textarea"],
    ["preferred_channels", { en: "Preferred channels of delivery: Notion / docs / email / recorded walk-through", es: "Canales de entrega preferidos: Notion / docs / email / walk-through grabado" }, "textarea"],
  ],
};

/* ─── Progress bar ─── */
function IntakeProgress({ step, t }) {
  const steps = [
    { key: 1, label: t("About you", "Sobre ti") },
    { key: 2, label: t("Positioning & goals", "Posicionamiento y objetivos") },
    { key: 3, label: t("Materials", "Materiales") },
  ];
  return (
    <div className="intake-progress" data-testid="intake-progress">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className={`ip-step ${step === s.key ? "current" : step > s.key ? "done" : ""}`} data-testid={`ip-step-${s.key}`}>
            <span className="ip-num">{step > s.key ? "✓" : s.key}</span>
            <span className="ip-label">{s.label}</span>
          </div>
          {i < steps.length - 1 && <div className={`ip-connect ${step > s.key ? "done" : ""}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─── Project timeline (mini) ─── */
function ProjectTimeline({ currentStatus, t }) {
  const currentIdx = STATUS_ORDER.indexOf(currentStatus || "intake_pending");
  return (
    <div className="project-timeline" data-testid="project-timeline">
      <div className="pt-kicker">{t("Your project", "Tu proyecto")}</div>
      <div className="pt-track">
        {STATUS_ORDER.map((s, i) => (
          <div key={s} className={`pt-step ${i === currentIdx ? "current" : i < currentIdx ? "done" : ""}`}>
            <span className="pt-dot" />
            <span className="pt-name">{STATUS_LABELS[s]?.en || s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Field renderer ─── */
function Field({ k, label, type, value, onChange, lang }) {
  const l = label[lang] || label.en;
  return (
    <div className="form-row" key={k}>
      <label htmlFor={`intake-${k}`}>{l}</label>
      {type === "input"
        ? <input id={`intake-${k}`} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`intake-${k}`} />
        : <textarea id={`intake-${k}`} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`intake-${k}`} rows={3} />
      }
    </div>
  );
}

/* ─── File upload panel (step 3) ─── */
function FileUploadSection({ orderId, t }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/files`);
      setFiles(data);
    } catch (e) { void e; }
  }, [orderId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setErr(""); setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/orders/${orderId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e2) {
        setErr(`${f.name}: ${e2?.response?.data?.detail || e2.message}`);
      }
    }
    setUploading(false);
    e.target.value = "";
    await refresh();
  };

  const remove = async (fileId) => {
    try { await api.delete(`/orders/${orderId}/files/${fileId}`); await refresh(); }
    catch (e) { void e; }
  };

  return (
    <div className="form-section" data-testid="intake-files-section">
      <div className="head">
        <h3>{t("Brand materials", "Materiales de marca")}</h3>
        <div className="form-kicker">{t("Logo, deck, PDFs, brand assets — up to 25 MB each", "Logo, deck, PDFs, activos de marca — hasta 25 MB cada uno")}</div>
      </div>
      <div className="body">
        <div className="upload-zone">
          <label className="upload-btn" data-testid="intake-upload-btn">
            <input type="file" multiple onChange={onPick} disabled={uploading} style={{ display: "none" }}
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.ai,.psd,.fig,.sketch,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.zip" />
            {uploading ? t("Uploading…", "Subiendo…") : t("Select files", "Seleccionar archivos")}
          </label>
          <span className="upload-hint">{t("Accepted: JPG · PNG · PDF · AI · PSD · DOC · PPT · ZIP", "Aceptados: JPG · PNG · PDF · AI · PSD · DOC · PPT · ZIP")}</span>
        </div>
        {err && <div className="banner-warning" style={{ marginTop: 12 }}>{err}</div>}
        {files.length > 0 && (
          <ul className="file-list" data-testid="intake-file-list">
            {files.map((f) => (
              <li key={f.file_id} className="file-row">
                <span className="file-name">{f.original_filename}</span>
                <span className="file-size">{(f.size / 1024).toFixed(0)} KB</span>
                <button type="button" className="file-remove" onClick={() => remove(f.file_id)} data-testid={`file-remove-${f.file_id}`}>{t("Remove", "Eliminar")}</button>
              </li>
            ))}
          </ul>
        )}
        <p className="intake-hint">{t("Optional — you can also send materials later from your dashboard.", "Opcional — también puedes enviar materiales luego desde tu panel.")}</p>
      </div>
    </div>
  );
}

/* ─── Main page ─── */
export default function IntakePage() {
  const { orderId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [universal, setUniversal] = useState({});
  const [specific, setSpecific] = useState({});
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  useSeo({ title: "Project intake — Brand and Behavior", noindex: true });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/intake/${orderId}`)}`, { replace: true }); return; }
    api.get(`/orders/${orderId}`)
      .then((r) => { setOrder(r.data); setLoading(false); })
      .catch(() => { setErr(t("Could not load this order.", "No se pudo cargar este pedido.")); setLoading(false); });
  }, [authLoading, user, orderId, navigate, t]);

  const setU = (k, v) => setUniversal((s) => ({ ...s, [k]: v }));
  const setS = (k, v) => setSpecific((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setSubmitting(true); setErr("");
    try {
      await api.post(`/orders/${orderId}/intake`, { universal, service_specific: specific });
      setDone(true);
    } catch (e) {
      setErr(t("Could not submit. Try again.", "No se pudo enviar. Inténtalo de nuevo."));
      void e;
    } finally { setSubmitting(false); }
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const next = () => { setStep((s) => Math.min(3, s + 1)); scrollTop(); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); scrollTop(); };

  if (loading || authLoading) return <main className="app-main"><section className="page-hero"><h1>{t("Loading…", "Cargando…")}</h1></section></main>;
  if (err && !order) return <main className="app-main"><section className="page-hero"><h1>{err}</h1></section></main>;
  if (!order) return null;

  if (done) {
    return (
      <main className="app-main" data-testid="intake-done">
        <section className="page-hero">
          <div className="kicker">{order.service_name}</div>
          <h1><em className="it">{t("Intake received.", "Cuestionario recibido.")}</em></h1>
          <p>{t("Your project is now in our review queue. We'll get back to you within 1–2 business days.", "Tu proyecto está en cola de revisión. Te responderemos en 1–2 días hábiles.")}</p>
        </section>
        <section style={{ padding: "0 22px 80px" }}>
          <ProjectTimeline currentStatus="in_review" t={t} />
          <button className="button" style={{ marginTop: 24 }} onClick={() => navigate("/dashboard")} data-testid="go-dashboard">{t("Open dashboard", "Abrir panel")}</button>
        </section>
      </main>
    );
  }

  if (order.payment_status !== "paid") {
    return (
      <main className="app-main">
        <section className="page-hero">
          <Link to="/dashboard" className="back-link" data-testid="back-to-dashboard">← {t("Back to dashboard", "Volver al panel")}</Link>
          <h1>{t("Payment not confirmed yet", "Pago aún no confirmado")}</h1>
          <p>{t("We could not confirm your payment for this order yet. It can take a minute. Click refresh to check again.", "Aún no pudimos confirmar tu pago. A veces tarda un minuto. Pulsa refrescar para revisar de nuevo.")}</p>
          <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
            <button className="button" onClick={() => window.location.reload()} data-testid="refresh-status">{t("Refresh status", "Refrescar estado")}</button>
            <Link to="/dashboard" className="button" data-testid="back-dashboard-btn">{t("Open dashboard", "Abrir panel")}</Link>
          </div>
        </section>
      </main>
    );
  }

  if (order.status !== "intake_pending" && order.intake_universal) {
    return (
      <main className="app-main">
        <section className="page-hero">
          <div className="kicker">{order.service_name}</div>
          <h1><em className="it">{t("Intake already submitted.", "Cuestionario ya enviado.")}</em></h1>
          <p>{t("Track progress from your dashboard.", "Sigue el progreso desde tu panel.")}</p>
        </section>
        <section style={{ padding: "0 22px 80px" }}>
          <button className="button" onClick={() => navigate("/dashboard")} data-testid="go-dashboard">{t("Open dashboard", "Abrir panel")}</button>
        </section>
      </main>
    );
  }

  const specific_fields = STEP_2_SPECIFIC[order.service_slug] || [];

  return (
    <main className="app-main" data-testid="intake-page">
      <section className="page-hero intake-hero">
        <Link to="/dashboard" className="back-link" data-testid="back-to-dashboard">← {t("Back to dashboard", "Volver al panel")}</Link>
        <div className="kicker">{order.service_name} · {t("Brief", "Brief")}</div>
        <h1>{t("Tell us about ", "Cuéntanos sobre ")}<em className="it">{t("your brand", "tu marca")}</em>.</h1>
        <p>{t("This replaces a kickoff call. The more context you give, the better the work.", "Esto reemplaza la llamada de kickoff. Cuanto más contexto nos des, mejor el trabajo.")}</p>
        <IntakeProgress step={step} t={t} />
        <ProjectTimeline currentStatus="intake_pending" t={t} />
      </section>

      <div className="form-card" data-testid="intake-form">
        {step === 1 && (
          <div className="form-section">
            <div className="head">
              <h3>{t("About you & the business", "Sobre ti y el negocio")}</h3>
              <div className="form-kicker">{t("Step 1 of 3", "Paso 1 de 3")}</div>
            </div>
            <div className="body">
              {STEP_1_FIELDS.map(([k, label, type]) => (
                <Field key={k} k={k} label={label} type={type} value={universal[k]} onChange={(v) => setU(k, v)} lang={lang} />
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="form-section">
              <div className="head">
                <h3>{t("Positioning, competition & goals", "Posicionamiento, competencia y objetivos")}</h3>
                <div className="form-kicker">{t("Step 2 of 3 · Universal", "Paso 2 de 3 · Universal")}</div>
              </div>
              <div className="body">
                {STEP_2_UNIVERSAL.map(([k, label, type]) => (
                  <Field key={k} k={k} label={label} type={type} value={universal[k]} onChange={(v) => setU(k, v)} lang={lang} />
                ))}
              </div>
            </div>
            {specific_fields.length > 0 && (
              <div className="form-section">
                <div className="head">
                  <h3>{order.service_name}</h3>
                  <div className="form-kicker">{t("Service-specific", "Específico del servicio")}</div>
                </div>
                <div className="body">
                  {specific_fields.map(([k, label, type]) => (
                    <Field key={k} k={k} label={label} type={type} value={specific[k]} onChange={(v) => setS(k, v)} lang={lang} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <FileUploadSection orderId={order.order_id} t={t} />
            <div className="form-section">
              <div className="head">
                <h3>{t("Ready to submit", "Listo para enviar")}</h3>
                <div className="form-kicker">{t("Step 3 of 3", "Paso 3 de 3")}</div>
              </div>
              <div className="body">
                <p className="intake-summary">
                  {t(
                    "Once you submit, your project moves to our review queue. We'll get back to you within 1–2 business days with next steps.",
                    "Al enviar, tu proyecto pasa a nuestra cola de revisión. Te responderemos en 1–2 días hábiles con los siguientes pasos."
                  )}
                </p>
              </div>
            </div>
          </>
        )}

        {err && <div className="banner-warning" style={{ margin: "0 22px 12px" }}>{err}</div>}

        <div className="intake-nav">
          {step > 1 && (
            <button type="button" className="button ghost" onClick={back} data-testid="intake-back">← {t("Back", "Atrás")}</button>
          )}
          <div style={{ flex: 1 }} />
          {step < 3 && (
            <button type="button" className="button" onClick={next} data-testid="intake-next">{t("Continue", "Continuar")} →</button>
          )}
          {step === 3 && (
            <button type="button" className="button" disabled={submitting} onClick={submit} data-testid="intake-submit">
              {submitting ? t("Submitting…", "Enviando…") : t("Submit intake", "Enviar cuestionario")}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
